var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback } from 'react';
import { useFileMethods, useUploadManager } from '../../UploadManagerProvider';
import { FileTypeNotSupportedError, MaxUploadsExceededError } from '../errors';
import { MaxFileSizeExceededError } from '../errors/MaxFileSizeExceededError';
import { useUploadConfiguration } from '../UploadConfigurationProvider';
const extension = (fileName) => {
    const splitName = fileName.split('.');
    return splitName[splitName.length - 1].toLocaleLowerCase();
};
const BYTES_TO_KB = 1024;
/**
 * Hook returns a function uploadFiles for uploading multiple files
 */
export const useUploadFiles = () => {
    const { uploadFile } = useFileMethods();
    const { onUpload, onError } = useUploadManager();
    const { supportedFileTypes: { fileExtensions, fileExtensionsAsString }, } = useUploadConfiguration();
    const { maxFileSizeInKilobytes, maxUploads } = useUploadConfiguration();
    const uploadFiles = useCallback((files) => __awaiter(void 0, void 0, void 0, function* () {
        if (maxUploads > 0 && files.length > maxUploads) {
            const error = new MaxUploadsExceededError(`Only ${maxUploads} can be uploaded at a time.`, files);
            if (onError) {
                onError(error);
            }
            else {
                throw error;
            }
        }
        else if (files && files.length > 0) {
            try {
                const uploads = [];
                const uploadedBlobs = [];
                const nonUploadedBlobs = [];
                for (let i = 0; i < files.length; i += 1) {
                    const file = files[i];
                    if (!fileExtensions.includes(extension(file === null || file === void 0 ? void 0 : file.name))) {
                        nonUploadedBlobs.push(file);
                        const error = new FileTypeNotSupportedError(`"${file === null || file === void 0 ? void 0 : file.name}" is not using a supported extension (${fileExtensionsAsString}).`, file);
                        if (onError) {
                            onError(error);
                        }
                        else {
                            throw error;
                        }
                    }
                    else if (maxFileSizeInKilobytes && file.size / BYTES_TO_KB > maxFileSizeInKilobytes) {
                        nonUploadedBlobs.push(file);
                        const error = new MaxFileSizeExceededError(`"${file === null || file === void 0 ? void 0 : file.name}" exceeds the max file size ${maxFileSizeInKilobytes} File size ${file.size / BYTES_TO_KB}.`, file);
                        if (onError) {
                            onError(error);
                        }
                        else {
                            throw error;
                        }
                    }
                    else {
                        const promise = uploadFile(file);
                        uploads.push(promise);
                        uploadedBlobs.push(file);
                    }
                }
                onUpload === null || onUpload === void 0 ? void 0 : onUpload(uploads, uploadedBlobs, nonUploadedBlobs);
                yield Promise.all(uploads);
            }
            catch (e) {
                if (onError) {
                    onError(e);
                }
                else {
                    throw e;
                }
            }
        }
    }), [uploadFile, fileExtensions, fileExtensionsAsString, onError, onUpload, maxUploads, maxFileSizeInKilobytes]);
    return uploadFiles;
};
//# sourceMappingURL=useUploadFiles.js.map